<template>
  <div class="rp-rounds-tab">
    <div class="rp-round-stats">
      <div class="rp-kpi"><b>{{ rounds.length }}</b><span>{{ $t('projects.recurring.kpi.rounds') }}</span></div>
      <div class="rp-kpi"><b>{{ running.length }}</b><span>{{ $t('projects.recurring.rounds_tab.running') }}</span></div>
      <div class="rp-kpi"><b>{{ closed.length }}</b><span>{{ $t('projects.recurring.rounds_tab.closed') }}</span></div>
      <div class="rp-kpi" :class="{'rp-kpi--red': overdue.length}"><b>{{ overdue.length }}</b><span>{{ $t('projects.management.stats.overdue') }}</span></div>
    </div>

    <div class="pm-card">
      <div class="pm-card__head">
        <h3>{{ $t('projects.recurring.rounds') }}</h3>
        <span class="pm-card__sub">{{ $t('projects.recurring.rounds_sub') }}</span>
        <span class="grow"></span>
        <div class="pm-seg pm-seg--sm">
          <div :class="{'on': filter === 'all'}" @click="filter = 'all'" qid="rounds-filter-all">{{ $t('projects.management.all') }}</div>
          <div :class="{'on': filter === 'running'}" @click="filter = 'running'" qid="rounds-filter-running">{{ $t('projects.recurring.rounds_tab.running') }}</div>
          <div :class="{'on': filter === 'closed'}" @click="filter = 'closed'" qid="rounds-filter-closed">{{ $t('projects.recurring.rounds_tab.closed') }}</div>
        </div>
        <b-button variant="success"
                  size="sm"
                  class="ml-2"
                  v-if="canRun"
                  @click="$emit('run')"
                  qid="rounds-run">
          <font-awesome-icon icon="play" class="mr-1"/>{{ $t('projects.recurring.run_round') }}
        </b-button>
      </div>
      <table class="pm-table">
        <thead>
          <tr>
            <th style="width:34%">{{ $t('projects.recurring.rounds_tab.round') }}</th>
            <th>{{ $t('projects.recurring.rounds_tab.period') }}</th>
            <th v-if="isAudit">{{ $t('projects.recurring.schedule.data_due_after') }}</th>
            <th>{{ $t('projects.management.columns.progress') }}</th>
            <th>{{ $t('projects.management.columns.status') }}</th>
            <th>{{ $t('projects.management.columns.team') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading"><td :colspan="isAudit ? 7 : 6" class="text-muted"><font-awesome-icon icon="spinner" class="fa-spin mr-2"/>{{ $t('projects.management.loading') }}</td></tr>
          <tr v-if="!loading && !visible.length"><td :colspan="isAudit ? 7 : 6" class="text-muted">{{ $t('projects.recurring.no_rounds') }}</td></tr>
          <tr v-for="round in visible" :key="round.slug" qid="rounds-row">
            <td>
              <div class="pm-name">
                <span class="rp-round-no" :class="'rp-round-no--' + state(round)">{{ round.number }}</span>
                <div class="grow">
                  <router-link :to="{name: 'projects.view', params: {slug: round.slug}}" class="pm-name__title">{{ round.title }}</router-link>
                  <div class="pm-name__ctx">{{ $t('projects.recurring.rounds_tab.updated', {date: formatDate(round.updated_at)}) }}</div>
                </div>
              </div>
            </td>
            <td class="pm-due">{{ formatDate(round.start_date) }} → <span :class="{'pm-due--red': isOverdue(round)}">{{ formatDate(round.end_date) }}</span></td>
            <td class="pm-due" v-if="isAudit">{{ formatDate(round.data_collection_end_date) }}</td>
            <td><ProgressBar :value="round.progress" :tone="isOverdue(round) ? 'red' : ''"/></td>
            <td><span class="pm-tag" :class="'pm-tag--' + statusTone(round)">{{ statusText(round) }}</span></td>
            <td><Team :users="round.users"/></td>
            <td class="text-right text-nowrap">
              <span class="pm-row-action pm-row-action--neutral"
                    v-if="canCopy"
                    @click="openCopy(round)"
                    v-b-tooltip.hover
                    :title="$t('projects.recurring.rounds_tab.copy')"
                    qid="rounds-copy">
                <font-awesome-icon icon="copy"/>
              </span>
              <span class="pm-row-action"
                    v-if="canDelete"
                    @click="openDelete(round)"
                    v-b-tooltip.hover
                    :title="$t('activities.delete.title')"
                    qid="rounds-delete">
                <font-awesome-icon icon="trash-alt"/>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Copy a round: a new round that starts from the chosen one -->
    <b-modal ref="copy-round"
             hide-header
             hide-footer
             centered
             modal-class="project-launcher-modal">
      <div class="project-launcher">
        <div class="project-launcher__head">
          <div class="grow">
            <h2>{{ $t('projects.recurring.rounds_tab.copy') }}</h2>
            <div class="project-launcher__sub">{{ $t('projects.recurring.rounds_tab.copy_sub', {title: toCopy.title}) }}</div>
          </div>
          <span class="project-launcher__close" @click="$refs['copy-round'].hide()"><font-awesome-icon icon="times"/></span>
        </div>
        <div class="project-launcher__body">
          <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
          <div class="project-launcher__field">
            <label>{{ $t('projects.launcher.name_label') }}</label>
            <div class="project-launcher__input"><input type="text"
                 v-model="copy.title"
                 maxlength="255"
                 qid="rounds-copy-title"></div>
          </div>
          <div class="project-launcher__field">
            <label>{{ $t('projects.launcher.when') }}</label>
            <div class="project-launcher__inline">
              <div class="project-launcher__control project-launcher__date">
                <font-awesome-icon icon="calendar-alt" class="project-launcher__control-icon"/>
                <datepicker v-model="copy.start_date"
                            format="d MMM yyyy"
                            :monday-first="true"
                            calendar-class="project-launcher__calendar"></datepicker>
              </div>
              <font-awesome-icon icon="arrow-right" class="text-muted"/>
              <div class="project-launcher__control project-launcher__date">
                <font-awesome-icon icon="calendar-alt" class="project-launcher__control-icon"/>
                <datepicker v-model="copy.due_date"
                            format="d MMM yyyy"
                            :monday-first="true"
                            calendar-class="project-launcher__calendar"></datepicker>
              </div>
            </div>
          </div>
          <div class="project-launcher__field" v-if="isAudit">
            <label>{{ $t('projects.recurring.schedule.data_due_after') }}</label>
            <div class="project-launcher__control project-launcher__date">
              <font-awesome-icon icon="calendar-alt" class="project-launcher__control-icon"/>
              <datepicker v-model="copy.data_collection_due_date"
                          format="d MMM yyyy"
                          :monday-first="true"
                          calendar-class="project-launcher__calendar"></datepicker>
            </div>
          </div>
          <div class="project-launcher__field">
            <label>{{ $t('projects.create.message_label') }}</label>
            <b-form-textarea v-model="copy.schedule_message" rows="3" max-rows="6"></b-form-textarea>
          </div>
          <b-form-checkbox v-model="copy.include_attachments" :value="1" :unchecked-value="0">{{ $t('system.include_project_attachments') }}</b-form-checkbox>
        </div>
        <div class="project-launcher__foot">
          <span class="grow"></span>
          <b-button variant="secondary" @click="$refs['copy-round'].hide()">{{ $t('projects.create.cancel') }}</b-button>
          <b-button variant="success"
                    class="ml-3"
                    :disabled="saving"
                    @click="saveCopy"
                    qid="rounds-copy-save">
            <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>{{ $t('projects.recurring.rounds_tab.copy') }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal ref="delete-round" hide-footer>
      <div slot="modal-title"><h4>{{ $t('activities.delete.title') }}</h4></div>
      <h5>{{ $t('activities.delete.description') }} {{ toDelete.title }}</h5>
      <h5>{{ $t('activities.delete.are_you_sure') }}</h5>
      <div class="text-right">
        <b-button variant="secondary" @click="$refs['delete-round'].hide()">{{ $t('activities.delete.cancel') }}</b-button>
        <b-button class="ml-3"
                  variant="danger"
                  @click="deleteRound"
                  qid="rounds-delete-confirm">{{ $t('activities.delete.submit') }}</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Datepicker from 'vuejs-datepicker';
  import ProgressBar from '@/views/Projects/Management/ProgressBar.vue';
  import Team from '@/views/Projects/Management/Team.vue';

  // Rounds tab of a recurring project: every child project with its progress, and copy and delete (US 9396)
  export default {
    name: 'RecurringRounds',
    components: {
      Datepicker,
      ProgressBar,
      Team
    },
    props: {
      project: {type: Object, required: true},
      canRun: {type: Boolean, default: false}
    },
    data: function() {
      return {
        loading: true,
        saving: false,
        error: '',
        filter: 'all',
        rounds: [],
        toCopy: {},
        toDelete: {},
        copy: {}
      }
    },
    computed: {
      isAudit() {
        return this.project.type === 'control_assessment';
      },
      canCopy() {
        return this.isGranted([this.permissions.PROJECT_CREATE, this.permissions.PROJECT_CREATE_OWN], this.project);
      },
      canDelete() {
        return this.isGranted([this.permissions.PROJECT_DELETE, this.permissions.PROJECT_DELETE_OWN], this.project);
      },
      running() {
        return _.filter(this.rounds, (round) => this.state(round) !== 'closed');
      },
      closed() {
        return _.filter(this.rounds, (round) => this.state(round) === 'closed');
      },
      overdue() {
        return _.filter(this.rounds, (round) => this.isOverdue(round));
      },
      // Newest round first
      visible() {
        let list = this.filter === 'running' ? this.running : (this.filter === 'closed' ? this.closed : this.rounds);
        return _.reverse(list.slice());
      }
    },
    methods: {
      formatDate(value) {
        var moment = require('moment');
        return value ? moment(String(value).substring(0, 10)).format('D MMM YYYY') : '—';
      },
      state(round) {
        return round.status === 'completed' || round.status === 'closed' ? 'closed' : 'running';
      },
      isOverdue(round) {
        var moment = require('moment');
        return this.state(round) === 'running' && !!round.end_date && moment(round.end_date).endOf('day').isBefore(moment());
      },
      statusText(round) {
        if (this.isOverdue(round)) {
          return this.$t('projects.management.overdue');
        }
        let labels = {'pending': 'system.pending', 'in progress': 'system.in_progress', 'completed': 'projects.button.completed_label', 'closed': 'projects.button.closed_label'};
        return labels[round.status] ? this.$t(labels[round.status]) : '—';
      },
      statusTone(round) {
        if (this.isOverdue(round)) {
          return 'red';
        }
        return {'in progress': 'teal', 'pending': 'amber'}[round.status] || 'grey';
      },
      loadRounds() {
        this.loading = true;
        piincHttp.get('projects/' + this.project.slug + '/projects', {params: {per_page: 200, sort: 'start_date', order: 'asc'}}).then((response) => {
          this.rounds = _.map(response.data.items, function(round, index) {
            return Object.assign({number: index + 1}, round);
          });
          this.loading = false;
        }, () => {
          this.loading = false;
        });
      },
      // Due dates follow the parent's "due after" settings, like the old copy dialog
      offset(every, interval) {
        var moment = require('moment');
        let units = {Days: 'day', Months: 'month', Years: 'year'};
        return every && units[interval] ? moment().add(every, units[interval]).toDate() : moment().add(14, 'day').toDate();
      },
      openCopy(round) {
        var moment = require('moment');
        this.error = '';
        this.toCopy = round;
        this.copy = {
          title: this.project.title + ' ' + moment().format('DD MMM YYYY'),
          start_date: moment().toDate(),
          due_date: this.offset(this.project.due_after, this.project.due_after_interval),
          data_collection_due_date: this.offset(this.project.data_collection_due_after, this.project.data_collection_due_after_interval),
          schedule_message: this.project.schedule_message || '',
          include_attachments: 1
        };
        this.$refs['copy-round'].show();
      },
      saveCopy() {
        var moment = require('moment');
        let start = moment(this.copy.start_date).format('YYYY-MM-DD');
        let due = moment(this.copy.due_date).format('YYYY-MM-DD');
        if (!this.copy.title || !this.copy.start_date || !this.copy.due_date || start > due) {
          this.error = this.$t('projects.schedule.start_date_greater_error');
          return;
        }
        let formData = new FormData();
        formData.append('title', this.copy.title);
        formData.append('compliance_activity', this.project.slug);
        formData.append('start_date', start);
        formData.append('due_date', due);
        if (this.isAudit && this.copy.data_collection_due_date) {
          formData.append('data_collection_due_date', moment(this.copy.data_collection_due_date).format('YYYY-MM-DD'));
        }
        formData.append('include_attachments', this.copy.include_attachments);
        if (this.copy.schedule_message) {
          formData.append('schedule_message', this.copy.schedule_message);
        }
        this.saving = true;
        piincHttp.post('projects/' + this.toCopy.slug + '/clone', formData).then(() => {
          this.saving = false;
          this.$refs['copy-round'].hide();
          this.$toastr('success', this.$t('projects.clone.success'));
          this.loadRounds();
          this.$emit('changed');
        }, (error) => {
          this.saving = false;
          this.error = error.data && error.data.message ? error.data.message : this.$t('projects.launcher.error_generic');
        });
      },
      openDelete(round) {
        this.toDelete = round;
        this.$refs['delete-round'].show();
      },
      deleteRound() {
        piincHttp.delete('projects/' + this.toDelete.slug).then(() => {
          this.$refs['delete-round'].hide();
          this.$toastr('success', this.$t('projects.project.delete.success'));
          this.loadRounds();
          this.$emit('changed');
        }, () => {});
      }
    },
    created() {
      this.loadRounds();
    }
  }
</script>
